import { useNavigate } from "react-router-dom";
import { SlLocationPin } from "react-icons/sl";
import { FiPlus } from "react-icons/fi";
import { LiaShippingFastSolid } from "react-icons/lia";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  changeOutlet,
  getDetailUserAndListOutlet,
} from "../../services/profileApi";
import { Map, Marker } from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateTokens } from "../../redux/features/auth/authSlice";
import { useNotification } from "../../components/context/NotificationContext";
import HOC from "../../components/HOC";
import {
  readSafeStorage,
  writeSafeStorage,
} from "../../utils/localStorageHelper";
import { IoArrowBack } from "react-icons/io5";

const INITIAL_LAT = -6.175;
const INITIAL_LNG = 106.8266;
const INITIAL_ZOOM = 15;

const SetLocationAddress = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [latLng, setLatLng] = useState(null);
  const [cameraProps, setCameraProps] = useState({
    center: { lat: INITIAL_LAT, lng: INITIAL_LNG },
    zoom: INITIAL_ZOOM,
  });
  const dispatch = useDispatch();
  const [companyId, setCompanyId] = useState(readSafeStorage("companyId", ""));

  const handleChangeOutlet = (newVal) => {
    setCompanyId(newVal);
    writeSafeStorage("companyId", newVal);

    mutateChange({
      outlet_id: newVal,
      company_id: Number(companyId),
    });
  };

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["detail-list-outlet"],
    queryFn: () => getDetailUserAndListOutlet(Number(companyId)),
    staleTime: 0,
    refetchOnMount: true,
  });

  const { mutate: mutateChange } = useMutation({
    mutationFn: changeOutlet,
    onSuccess: (tokenData) => {
      if (tokenData) {
        dispatch(
          updateTokens({
            accessToken: tokenData.access,
            accessExpired: tokenData.access_expired,
            renewToken: tokenData.renew,
            renewExpired: tokenData.renew_expired,
          }),
        );
      }
      showNotification("Berhasil mengubah outlet", "success");
      navigate("/");
      refetch();
    },
    onError: () => {
      showNotification("Gagal mengubah outlet", "error");
      refetch();
    },
  });

  useEffect(() => {
    if (data) {
      setLatLng({
        lat: data.user_address?.latitude,
        lng: data.user_address?.longitude,
      });

      setCameraProps({
        center: {
          lat: data.user_address?.latitude,
          lng: data.user_address?.longitude,
        },
        zoom: INITIAL_ZOOM,
      });
    }
  }, [data]);

  return (
    <div className="w-full md:mt-4 min-h-screen">
      <div className="flex justify-center min-h-screen">
        <div className="w-full max-w-7xl mx-auto md:px-5 lg:px-0">
          <div className="hidden md:block mb-6 mt-4">
            <nav className="text-base text-slate-600 mb-3 flex flex-wrap items-center gap-1 font-medium">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                Beranda
              </button>
              <span className="text-slate-400">/</span>
              <span className="text-primary">Lokasi & Pengiriman</span>
            </nav>

            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-semibold text-slate-800 tracking-tight">
                  Atur Lokasi & Pengiriman
                </h1>

                <p className="text-slate-500 mt-1 text-sm lg:text-base">
                  Silahkan atur lokasi tujuan dan pilih apotek pengirim
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-md font-medium text-slate-700 shadow-sm hover:bg-secondaryActive hover:border-secondary/30 hover:text-secondary transition-colors"
              >
                <IoArrowBack size={18} />
                Kembali
              </button>
            </div>
          </div>

          {isFetching ? (
            <div className="text-center py-10">Loading...</div>
          ) : (
            <div className="flex flex-col lg:grid lg:grid-cols-[1.1fr_0.9fr] gap-5 lg:gap-4">
              <div className="bg-white rounded-3xl md:border md:border-slate-200 md:shadow-sm p-3 md:p-5">
                <h1 className="font-semibold text-lg mb-3 text-slate-700">
                  Alamat Saya (Penerima)
                </h1>

                {data?.user_address?.location_id !== 0 ? (
                  <>
                    <div className="rounded-xl border border-gray-200 p-3 mb-4">
                      <div className="flex gap-2">
                        <div className="shrink-0 flex-none pt-1">
                          <SlLocationPin className="text-error w-5 h-5 md:w-7 md:h-7 lg:w-8 lg:h-8" />
                        </div>

                        <div className="text-gray-600 min-w-0">
                          <h2 className="font-semibold text-base text-slate-700">
                            {data?.user_address?.username}
                          </h2>

                          <p className="text-primary font-medium my-1">
                            {data?.user_address?.phone_number}
                          </p>

                          <p>
                            {data?.user_address?.address} (
                            {data?.user_address?.detail_address})
                          </p>

                          {data?.user_address?.notes && (
                            <p>"{data?.user_address?.notes}"</p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 rounded-xl overflow-hidden">
                        <Map
                          id="google-map"
                          center={latLng}
                          defaultZoom={INITIAL_ZOOM}
                          {...cameraProps}
                          style={{
                            width: "100%",
                            height: window.innerWidth < 1024 ? "50vw" : "240px",
                            background: "#fffddd",
                          }}
                        >
                          <Marker position={latLng} />
                        </Map>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        navigate("/location-address/my-address?nearby=true")
                      }
                      className="h-[44px] border border-gray-300 bg-white text-gray-700 w-full font-medium rounded-lg hover:bg-gray-100 transition"
                    >
                      Ubah / Tambah Alamat
                    </button>
                  </>
                ) : (
                  <>
                    <div className="rounded-xl border-2 border-gray-200 flex flex-col items-center justify-center p-5 mb-4">
                      <div className="p-2 rounded-full bg-secondaryActive mb-2">
                        <SlLocationPin size={24} className="text-secondary" />
                      </div>

                      <h2 className="font-semibold text-slate-700">
                        Alamat Penerima Belum Diatur
                      </h2>

                      <p className="text-center text-gray-500 text-sm">
                        Tambahkan alamat tujuan sebelum melakukan pemesanan
                      </p>
                    </div>

                    <button
                      onClick={() => navigate("/location-address/my-address")}
                      className="h-[44px] border border-secondary bg-secondary text-white w-full rounded-lg font-medium hover:bg-secondaryHover transition flex items-center justify-center gap-2"
                    >
                      <FiPlus size={16} />
                      Tambah Alamat
                    </button>
                  </>
                )}
              </div>

              <div className="bg-white rounded-3xl md:border md:border-slate-200 md:shadow-sm p-3 md:p-5 h-fit lg:sticky lg:top-[82px]">
                <h1 className="font-semibold text-lg mb-3 text-slate-700">
                  Apotek Pengirim
                </h1>

                <div className="flex flex-col gap-3">
                  {data?.list_outlet?.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleChangeOutlet(item.outlet_id)}
                      className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition ${
                        item.is_selected
                          ? "border-secondary bg-secondaryActive"
                          : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex gap-3 flex-1">
                        <LiaShippingFastSolid
                          size={22}
                          className="text-secondary mt-1"
                        />

                        <div className="flex-1">
                          <h2 className="font-semibold text-base text-slate-700">
                            {item.outlet_name}
                          </h2>

                          <p className="text-slate-500 mb-2">{item.address}</p>

                          <p className="text-slate-500">
                            <span className="text-slate-700">
                              {data.user_address.location_id === 0
                                ? "-"
                                : item.distance}{" "}
                              km
                            </span>{" "}
                            Dari lokasi anda
                          </p>
                        </div>
                      </div>

                      <input
                        type="radio"
                        checked={item.is_selected}
                        readOnly
                        className="w-5 h-5 mt-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HOC(SetLocationAddress, "required-auth");
